/**
 * Availability Manager Component
 * Manage weekly schedule and time-off
 */

import React, { useState } from 'react';
import { format, parse } from 'date-fns';
import { useFormattedDate, useUserTimezone } from '../../hooks/useUserTimezone';
import { formatTimeString12h } from '../../utils/dateTime';
import {
  Clock,
  Plus,
  Trash2,
  Save,
  Calendar,
  AlertCircle,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  availabilityAPI,
  AvailabilitySlot,
  AvailabilityOverride,
  DAY_LABELS,
  LOCATION_TYPE_LABELS,
  LocationType,
  OverrideType,
} from '@/services/api/calendar';
import { cn } from '@/lib/utils';
import { useAppTranslation } from '@/contexts/TranslationContext';

const OVERRIDE_TYPE_LABELS: Record<OverrideType, string> = {
  unavailable: 'Unavailable',
  custom_hours: 'Custom Hours',
  holiday: 'Holiday',
  vacation: 'Vacation',
  sick: 'Sick Leave',
  training: 'Training',
};

export default function AvailabilityManager() {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  // The staff member's own timezone preference (falls back to the browser tz).
  // Availability slots are stored with the timezone they were entered in, so
  // they must carry the user's actual tz — not a hard-coded default (TRUS-258).
  const userTimezone = useUserTimezone();
  const queryClient = useQueryClient();
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [showAddOverride, setShowAddOverride] = useState(false);
  const [newSlot, setNewSlot] = useState({
    day_of_week: 1, // Monday
    start_time: '09:00',
    end_time: '17:00',
    slot_duration_minutes: 30,
    buffer_minutes: 0,
    default_location_type: 'in_person' as LocationType,
  });
  const [newOverride, setNewOverride] = useState({
    override_date: format(new Date(), 'yyyy-MM-dd'),
    override_type: 'unavailable' as OverrideType,
    reason: '',
    start_time: '09:00',
    end_time: '17:00',
  });

  // Fetch availability
  const { data: availability, isLoading } = useQuery({
    queryKey: ['my-availability'],
    queryFn: availabilityAPI.getMyAvailability,
  });

  // Mutations
  const createSlotMutation = useMutation({
    mutationFn: availabilityAPI.createSlot,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-availability'] });
      setShowAddSlot(false);
      setNewSlot({
        day_of_week: 1,
        start_time: '09:00',
        end_time: '17:00',
        slot_duration_minutes: 30,
        buffer_minutes: 0,
        default_location_type: 'in_person',
      });
    },
  });

  const deleteSlotMutation = useMutation({
    mutationFn: availabilityAPI.deleteSlot,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-availability'] });
    },
  });

  const createOverrideMutation = useMutation({
    mutationFn: availabilityAPI.createOverride,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-availability'] });
      setShowAddOverride(false);
      setNewOverride({
        override_date: format(new Date(), 'yyyy-MM-dd'),
        override_type: 'unavailable',
        reason: '',
        start_time: '09:00',
        end_time: '17:00',
      });
    },
  });

  const handleAddSlot = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createSlotMutation.mutateAsync({
        ...newSlot,
        timezone: userTimezone,
        is_active: true,
        max_appointments_per_slot: 1,
      });
    } catch (error) {
      console.error('Error creating slot:', error);
    }
  };

  const handleDeleteSlot = async (slotId: number) => {
    if (!confirm('Are you sure you want to delete this availability slot?')) return;
    try {
      await deleteSlotMutation.mutateAsync(slotId);
    } catch (error) {
      console.error('Error deleting slot:', error);
    }
  };

  const handleAddOverride = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createOverrideMutation.mutateAsync({
        override_date: newOverride.override_date,
        override_type: newOverride.override_type,
        reason: newOverride.reason || undefined,
        start_time: newOverride.override_type === 'custom_hours' ? newOverride.start_time : undefined,
        end_time: newOverride.override_type === 'custom_hours' ? newOverride.end_time : undefined,
      });
    } catch (error) {
      console.error('Error creating override:', error);
    }
  };

  // Group slots by day
  const slotsByDay = DAY_LABELS.map((day, index) => ({
    day,
    dayIndex: index,
    slots: availability?.weekly_schedule.filter((s) => s.day_of_week === index) || [],
  }));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Weekly Schedule */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Clock className="h-5 w-5 text-teal-600" />
              Weekly Schedule
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Set your regular working hours for each day
            </p>
          </div>
          <button
            onClick={() => setShowAddSlot(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Time Slot
          </button>
        </div>

        <div className="p-6">
          {/* Add Slot Form */}
          {showAddSlot && (
            <form
              onSubmit={handleAddSlot}
              className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-4"
            >
              <h3 className="font-medium text-gray-900 dark:text-white">New Availability Slot</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Day
                  </label>
                  <select
                    value={newSlot.day_of_week}
                    onChange={(e) =>
                      setNewSlot((prev) => ({ ...prev, day_of_week: parseInt(e.target.value) }))
                    }
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  >
                    {DAY_LABELS.map((day, index) => (
                      <option key={index} value={index}>
                        {day}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={newSlot.start_time}
                    onChange={(e) => setNewSlot((prev) => ({ ...prev, start_time: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={newSlot.end_time}
                    onChange={(e) => setNewSlot((prev) => ({ ...prev, end_time: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Slot Duration
                  </label>
                  <select
                    value={newSlot.slot_duration_minutes}
                    onChange={(e) =>
                      setNewSlot((prev) => ({
                        ...prev,
                        slot_duration_minutes: parseInt(e.target.value),
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  >
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={90}>1.5 hours</option>
                    <option value={120}>2 hours</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={createSlotMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {createSlotMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddSlot(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Weekly Schedule Grid */}
          <div className="grid grid-cols-7 gap-2">
            {slotsByDay.map(({ day, dayIndex, slots }) => (
              <div
                key={dayIndex}
                className={cn(
                  'p-3 rounded-lg border min-h-[120px]',
                  slots.length > 0
                    ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800'
                    : 'bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-600'
                )}
              >
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">{day}</p>
                {slots.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">Not available</p>
                ) : (
                  <div className="space-y-1">
                    {slots.map((slot) => (
                      <div
                        key={slot.id}
                        className="flex items-center justify-between text-xs bg-white dark:bg-gray-700 rounded px-2 py-1"
                      >
                        <span className="text-gray-700 dark:text-gray-300">
                          {formatTimeString12h(slot.start_time)} - {formatTimeString12h(slot.end_time)}
                        </span>
                        <button
                          onClick={() => slot.id && handleDeleteSlot(slot.id)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Time Off / Overrides */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Calendar className="h-5 w-5 text-teal-600" />
              Time Off & Exceptions
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Block specific dates or adjust hours
            </p>
          </div>
          <button
            onClick={() => setShowAddOverride(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Time Off
          </button>
        </div>

        <div className="p-6">
          {/* Add Override Form */}
          {showAddOverride && (
            <form
              onSubmit={handleAddOverride}
              className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-4"
            >
              <h3 className="font-medium text-gray-900 dark:text-white">Add Time Off</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={newOverride.override_date}
                    onChange={(e) =>
                      setNewOverride((prev) => ({ ...prev, override_date: e.target.value }))
                    }
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type
                  </label>
                  <select
                    value={newOverride.override_type}
                    onChange={(e) =>
                      setNewOverride((prev) => ({
                        ...prev,
                        override_type: e.target.value as OverrideType,
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  >
                    {Object.entries(OVERRIDE_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                {newOverride.override_type === 'custom_hours' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={newOverride.start_time}
                        onChange={(e) =>
                          setNewOverride((prev) => ({ ...prev, start_time: e.target.value }))
                        }
                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        End Time
                      </label>
                      <input
                        type="time"
                        value={newOverride.end_time}
                        onChange={(e) =>
                          setNewOverride((prev) => ({ ...prev, end_time: e.target.value }))
                        }
                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                      />
                    </div>
                  </>
                )}
                <div className={newOverride.override_type === 'custom_hours' ? 'col-span-2' : 'col-span-2'}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Reason (optional)
                  </label>
                  <input
                    type="text"
                    value={newOverride.reason}
                    onChange={(e) =>
                      setNewOverride((prev) => ({ ...prev, reason: e.target.value }))
                    }
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                    placeholder="e.g., Doctor's appointment"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={createOverrideMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {createOverrideMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddOverride(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Overrides List */}
          {availability?.overrides && availability.overrides.length > 0 ? (
            <div className="space-y-2">
              {availability.overrides.map((override) => (
                <div
                  key={override.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={cn(
                        'w-2 h-2 rounded-full',
                        override.override_type === 'unavailable' && 'bg-red-500',
                        override.override_type === 'custom_hours' && 'bg-blue-500',
                        override.override_type === 'vacation' && 'bg-purple-500',
                        override.override_type === 'sick' && 'bg-orange-500',
                        override.override_type === 'holiday' && 'bg-green-500',
                        override.override_type === 'training' && 'bg-teal-500'
                      )}
                    />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {fmt.custom(override.override_date, 'EEEE, MMMM d, yyyy')}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {OVERRIDE_TYPE_LABELS[override.override_type]}
                        {override.override_type === 'custom_hours' &&
                          override.start_time &&
                          override.end_time &&
                          ` (${formatTimeString12h(override.start_time)} - ${formatTimeString12h(override.end_time)})`}
                        {override.reason && ` - ${override.reason}`}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No time off scheduled</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
